// Field-repair resource economy (16.80, finalizing 16.4's PENDING table).
//
// 16.4 priced the field Repair module two ways that stopped agreeing once
// the fixed roster (16.16) replaced the hypothetical hulls it was written
// against: a RATE (Ferrite per 5s cycle) and a POOL (Ferrite per full hull
// repair). 16.12's time-axis rescale multiplied every HP pool by 10x while
// deliberately leaving every rate unscaled -- but its repair-economy note
// ("keep the same Ferrite-per-full-repair amounts, let each Ferrite restore
// 10x more HP") only rescaled the pool side, so the two columns drifted
// ~3.75x apart. Simulation (repairEconomy.test.ts) resolves it in favor of
// the rate side, which is the one 16.12's own rule says not to scale.
export const REPAIR_ORE = "Ferrite Ore";

// Solved backward from intent, not picked (CLAUDE.md): a full ore hold of
// Ferrite should carry a Small gunship -- the binding case, since it is the
// only Small hull with both a real repair module and a small ore hold --
// through roughly a session's worth of fighting, targeted at 5 average
// survived engagements. Marauder I: 9 m3 ore hold = 90 Ferrite units (0.1
// m3/unit, 16.40); simulated average survived engagement costs 708 HP of
// repair; 90 / 5 = 18 Ferrite per engagement -> 39 HP/Ferrite, rounded.
//
// This independently reconstructs 16.4's own per-cycle column: the Marauder
// line repairs 40/70/130/470 HP per 5s cycle at T1-T4, which at this rate is
// 1.0/1.8/3.3/11.8 Ferrite/cycle against 16.4's locked 1/2/5. The rate side
// of that table was right all along; only its full-repair column was stale.
export const HP_PER_FERRITE = 40;

export function ferriteForHp(hp: number): number {
  return Math.max(0, hp) / HP_PER_FERRITE;
}

export function hpForFerrite(units: number): number {
  return Math.max(0, units) * HP_PER_FERRITE;
}

// Ferrite cost of one full cycle of a given repair module, at the rate the
// roster actually gives that hull (PlayerShip.repairPerS) -- per-ship, not
// per-hull-size. Under the fixed roster a Defensive Marauder repairs 41%
// faster than an Aggressive one, so a flat size-based charge would bill
// them identically for different amounts of HP.
export function ferritePerCycle(repairPerS: number, cycleMs: number): number {
  return ferriteForHp(repairPerS * (cycleMs / 1000));
}

export interface RepairDraw {
  hpRepaired: number;
  ferriteSpent: number;
  // true when the hold could not cover the whole cycle -- the caller both
  // applies the partial repair and shuts the module down.
  starved: boolean;
}

// Partial draws are real: a cycle that can only afford 60% of its Ferrite
// repairs 60% of its HP rather than being refused outright, so the last of
// the ore is never stranded unusable in the hold.
export function drawRepair(requestedHp: number, ferriteAvailable: number): RepairDraw {
  const wanted = ferriteForHp(requestedHp);
  const available = Math.max(0, ferriteAvailable);
  if (wanted <= available) {
    return { hpRepaired: Math.max(0, requestedHp), ferriteSpent: wanted, starved: false };
  }
  return { hpRepaired: hpForFerrite(available), ferriteSpent: available, starved: true };
}
